import * as React from 'react'

import { usePageInfoContext } from '../../Contexts/PageInfoContext'
import TextButton from '../../atoms/Button/Text/Button'
import Image from '../../atoms/Image'
import Paragraph from '../../atoms/Paragraph'
import DisclaimerBlock from '../../molecules/Disclaimer'
import { mapCombinedLink } from '../../utils/mappings/general'
import { isRelativeUrl, isEmailAddress, isTelephoneNumber, isHashUrl } from '../../utils/url'

interface Props {
  content?: any
}

/**
 * Footer Component.
 * @param content - Content from the CMS.
 */
const Footer = ({ content }: Props) => {
  const { additionalFooterDisclaimer } = usePageInfoContext()
  const globalDisclaimers = content?.fields?.globalPageDisclaimers || []

  const pageDisclaimers = additionalFooterDisclaimer.concat(globalDisclaimers)

  const emergencyServicesMembersLink = mapCombinedLink(content?.fields?.emergencyServicesMembersLink?.fields)
  const stateSuperMembersLink = mapCombinedLink(content?.fields?.stateSuperMembersLink?.fields)

  return (
    <>
      <DisclaimerBlock border={true} fullWidth={false} content={pageDisclaimers} />
      <footer role="contentinfo" data-testid="footerBlock" className="layoutSpacing w-100 bg-purple-100 py-48">
        <div className="layout">
          <div className="mb-24 flex" data-testid="footerImage">
            <a href="/" aria-label="Go to homepage">
              <Image
                imageUrl={content?.fields?.footerLogo?.fields?.file?.url}
                altText={content?.footerLogo?.fields?.description}
                height="100"
                width="auto"
              />
            </a>
          </div>
          <div className="border-t border-purple-pressedDark pt-48">
            <div className="flex flex-col xl:flex-row">
              <div className="flex w-full flex-1 flex-col gap-32 xl:w-50 xl:flex-row xl:gap-48">
                <div>
                  <a
                    href={emergencyServicesMembersLink.href}
                    aria-label={emergencyServicesMembersLink.title}
                    className="inline-block w-max pb-24 text-2xl text-white underline lg:text-h3"
                  >
                    {emergencyServicesMembersLink.title}
                  </a>
                  <Paragraph content={content?.fields?.emergencyServicesMembers} classes="text-white" />
                </div>
                <div>
                  <a
                    href={stateSuperMembersLink.href}
                    aria-label={stateSuperMembersLink.title}
                    className="inline-block w-max pb-24 text-2xl text-white underline lg:text-h3"
                  >
                    {stateSuperMembersLink.title}
                  </a>
                  <Paragraph content={content?.fields?.stateSuperMembers} classes="text-white" />
                </div>
              </div>
              <div className="mt-32 w-full xl:mt-0 xl:w-50 xl:columns-2">
                {content?.fields?.footerMenus?.fields?.menusDropdown?.map((item, index) => (
                  <div key={`${item?.sys?.id}-${index}`}>
                    <TextButton
                      name={item?.fields?.menuName}
                      link={item?.fields?.menuLink}
                      variant="dark"
                      fontColor="text-white"
                      classes="mb-8"
                      underline={true}
                    />
                  </div>
                ))}
              </div>
            </div>
            <DisclaimerBlock
              fullWidth={true}
              content={content?.fields?.disclaimer}
              classes="text-white py-32 text-md w-100 xl:w-50 xl:pr-32"
            />
            {content?.fields?.mediaLinks?.length && (
              <div className="mt-24 flex flex-wrap gap-32">
                {content.fields.mediaLinks.map((item) => {
                  const linkFiled = mapCombinedLink(item?.fields?.link?.fields)
                  const linkHref = linkFiled.href
                  const isExternal =
                    !isRelativeUrl(linkHref) &&
                    !isEmailAddress(linkHref) &&
                    !isTelephoneNumber(linkHref) &&
                    !isHashUrl(linkHref)

                  const mediaContent = (
                    <Image
                      imageUrl={item?.fields?.image?.fields?.file?.url}
                      altText={item?.fields?.alternateText ?? item?.fields?.image?.fields?.description}
                    />
                  )
                  if (linkHref)
                    return (
                      <a
                        key={item.sys.id}
                        rel="noreferrer"
                        target={linkFiled.openLinkInNewTab || isExternal ? '_blank' : ''}
                        href={linkHref}
                        className="max-h-100 max-w-100"
                      >
                        {mediaContent}
                      </a>
                    )
                  return (
                    <div key={item.sys.id} className="max-h-100 max-w-100">
                      {mediaContent}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
          <div className="mt-48 flex flex-col justify-between border-t border-purple-pressedDark pt-48 xl:flex-row">
            <div className="flex flex-wrap md:gap-32">
              {content?.fields?.bottomMenu?.fields?.menusDropdown?.map((item, index) => (
                <div key={`${item?.sys?.id}-${index}`}>
                  <TextButton
                    name={item?.fields?.menuName}
                    link={item?.fields?.menuLink}
                    variant="dark"
                    fontColor="text-white"
                    classes="mb-24 xl:mb-0 underline"
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-32">
              {content?.fields?.socialMedia?.map((item) => (
                <div key={item?.sys?.id}>
                  <a
                    href={item?.fields?.socialMediaUrl}
                    aria-label={`link-${item?.fields?.socialMediaPlatform}`}
                    className="h-24 w-24"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Image
                      className="max-h-24 max-w-24"
                      imageUrl={item?.fields?.socialMediaIcon?.fields?.file?.url}
                      altText={item?.fields?.socialMediaPlatform}
                    />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </>
  )
}

export default Footer
